<template>
  <v-container fluid>
    <v-tabs color="transparent" v-model="tab" show-arrows>
      <v-tabs-slider color="primary"></v-tabs-slider>
      <v-flex v-if="isTenant">
        <v-tab v-for="tabItem in tabs" :key="tabItem">
          {{ tabItem }}
        </v-tab>
      </v-flex>
      <v-flex v-else>
        <v-tab v-for="tabItem in warehouseTabs" :key="tabItem">
          {{ tabItem }}
        </v-tab>
      </v-flex>
      <v-tabs-items v-model="tab" touchless>
        <v-tab-item v-for="(item, index) in 6" :key="'item' + index">
          <div v-if="tab === 0 && index === 0">
            <PaginationController
              v-model="forecasts"
              getDataActionName="getActiveForecasts"
              haveStartDate
              haveSearchBox
              keepKeywordCase
              :select="selectedValue"
              :startDateLabel="$t('processStartDate')"
              :searchBoxLabel="`${$t('tracking')} / ${$t('upc')} / sku / ${$t('boxMark')}`"
              :historyLimit="historyLimit"
            >
              <template v-slot:beforeSearchBox>
                <v-flex md4 v-if="isWarehouse">
                  <v-autocomplete
                    v-model="selectedValue"
                    :items="organizationSelections"
                    :label="$t('selectOrganization')"
                    item-text="displayName"
                    item-value="key"
                  ></v-autocomplete>
                </v-flex>
                <v-flex md4 v-else>
                  <v-autocomplete
                    v-model="selectedValue"
                    :items="[
                      { displayName: `-- ${$t('all')} --`, key: '' },
                      { displayName: $t('ready'), key: 'ready' },
                      { displayName: $t('partial'), key: 'partial' },
                    ]"
                    :label="$t('selectStatus')"
                    item-text="displayName"
                    item-value="key"
                  ></v-autocomplete>
                </v-flex>
              </template>
              <template v-slot:beforeMenu>
                <v-flex v-if="isTenant" md1 class="headline">
                  <v-btn
                    color="primary"
                    :disabled="!openForecastedWarehouseKey.length && !!tenantInfo.closePKGModeTime"
                    @click="addNewForecast()"
                  >
                    <v-icon dark>add</v-icon> {{ $t('forecast') }}</v-btn>
                </v-flex>
              </template>
              <template v-slot:dataTable>
                <v-data-table item-key="_key" expand :headers="headers"  :items="forecasts" hide-actions>
                  <template v-slot:headerCell="props">
                    <template v-if="props.header.text === 'quantity'">
                      <v-flex>{{ $t('forecast') }}</v-flex>
                      <v-flex>{{ $t('inboundQuantity') }}</v-flex>
                    </template>
                    <template v-else-if="props.header.text === 'time'">
                      <v-flex>{{ $t('createTime') }}</v-flex>
                      <v-flex>{{ $t('lastModifiedTime') }}</v-flex>
                    </template>
                    <template v-else>{{ props.header.text }}</template>
                  </template>
                  <template v-slot:items="props">
                    <tr @click="props.expanded = !props.expanded">
                      <td class="text-xs-center">{{ props.index + 1 }}</td>
                      <td class="text-xs-left">{{ props.item._key }}</td>
                      <td class="text-xs-left">
                        <div :style="props.item.isDisabledOrg ?'background-color: yellow': ''">
                          {{
                            isTenant
                              ? `${getWarehouseName(props.item.warehouseKey)}`
                              : `${getOrganizationName(props.item.organizationKey, props.item)}`
                          }}
                        </div>
                        <div>
                          {{ getWarehouseSiteName(props.item.warehouseKey, props.item.warehouseSite) }}
                        </div>
                      </td>
                      <td class="text-xs-left">
                        {{calculateTotalQty(props.item.pkgItems)}}
                      </td>
                      <td class="text-xs-left">
                        {{calculateSkuOfNum(props.item.pkgItems)}}
                      </td>
                      <td class="text-xs-left">
                        {{calculateBoxQty(props.item.pkgItems)}}
                      </td>
                      <td class="text-xs-left">
                        {{ $t(props.item.status === 'ready' ? 'readyLower' : props.item.status) }}
                      </td>
                      <td class="text-xs-left">
                        {{ props.item.shipType || 'N/A' }}
                      </td>
                      <td>
                        <div>{{ toDateYYYYMMDD(props.item.createTime) }}</div>
                        <div>{{ toDateYYYYMMDD(props.item.lastModifiedTime) }}</div>
                      </td>
                      <td style="white-space: pre-wrap; word-break: break-word; max-width: 300px; white-space: pre-line">
                        {{ props.item.note }}
                      </td>
                      <td class="text-xs-center" v-if="!isWarehouse">
                        <v-menu offset-y v-model="menu[String(props.item._key.slice(-5))]">
                          <template v-slot:activator="{ on }">
                            <v-tooltip top>
                              <template v-slot:activator="tooltip">
                                <div v-on="tooltip.on">
                                  <v-btn @click.stop="on.click" dark icon flat color="primary">
                                    <v-icon>build</v-icon>
                                  </v-btn>
                                </div>
                              </template>
                              {{ $t('more') }}
                            </v-tooltip>
                          </template>
                          <v-list>
                            <v-list-tile>
                              <v-btn flat @click.stop="showDetailDialog(props.item)" color="primary">
                                {{ $t('open') }}
                              </v-btn>
                            </v-list-tile>
                            <v-list-tile>
                              <v-btn
                                flat
                                :loading="props.item.isLoading"
                                :disabled="props.item.status === 'ready'"
                                @click.stop="confirmForecast(props.item)"
                                color="primary"
                              >
                                {{ $t('confirm') }}
                              </v-btn>
                            </v-list-tile>
                            <v-list-tile>
                              <v-btn
                                flat
                                :loading="props.item.isPrinting"
                                :disabled="props.item.isPrinting || !props.item.isDClass"
                                @click.stop="printBoxMarks(props.item)"
                                color="primary"
                              >
                                {{ $t('printBoxMark') }}
                              </v-btn>
                            </v-list-tile>
                            <v-list-tile>
                              <v-btn
                                flat
                                :disabled="props.item.status === 'partial'"
                                :loading="props.item.isDelLoading"
                                @click.stop="deleteForecast(props.item)"
                                color="primary"
                              >
                                {{ $t('delete') }}
                              </v-btn>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </td>
                      <td class="text-xs-center" v-if="isWarehouse">
                        <v-layout justify-space-between row>
                          <v-btn
                                flat
                                :loading="props.item.isLoading"
                                v-if="props.item.status === 'partial'"
                                :disabled="!isWarehouseOwner"
                                @click.stop="confirmForecast(props.item)"
                                color="primary"
                              >
                                {{ $t('confirm') }}
                          </v-btn>
                          <v-btn v-else :loading="props.item.isLoading" :disabled="!(isWarehouseOwner && props.item.isDisabledOrg)" @click.stop="deleteForecast(props.item)" flat color="primary">
                              {{ $t('delete') }}
                          </v-btn>
                        </v-layout>
                      </td>
                      
                    </tr>
                  </template>
                  <template v-slot:expand="props">
                  <ForecastTableExpand :item="props.item"></ForecastTableExpand>
                  </template>
                </v-data-table>
              </template>
            </PaginationController>
            <template v-if="!!featureInboundForecastDropshipping.length">
              <DropshippingForecastPopup
              v-model="isShowForecastDialog"
              v-if="isShowForecastDialog"
              :isAddForecast="isAddForecast"
              :forecast="forecast"
              @close="closeForecastDialog"
              @addForecast="addForecast"
              @updateForecast="updateForecast"
            />
            </template>
            <template v-else>
              <ForecastDetailPopup
                v-model="isShowForecastDialog"
                v-if="isShowForecastDialog"
                :isAddForecast="isAddForecast"
                :forecast="forecast"
                @close="closeForecastDialog"
                @addForecast="addForecast"
                @updateForecast="updateForecast"
              />
            </template>
            
          </div>
          <div v-if="tab === 1 && index === 1">
            <PaginationController
              v-model="forecasts"
              getDataActionName="getHistoryForecasts"
              haveStartDate
              haveSearchBox
              keepKeywordCase
              :select="selectedStatus"
              :startDateLabel="$t('processStartDate')"
              :isRefreshSelect="false"
              :searchBoxLabel="`${$t('tracking')} / ${$t('upc')} / sku / ${$t('boxMark')}`"
              :historyLimit="historyLimit"
            >
              <template v-slot:beforeSearchBox>
                <v-flex md4>
                  <v-autocomplete
                    v-model="selectedStatus"
                    :items="[
                      { displayName: `-- ${$t('all')} --`, key: '' },
                      { displayName: $t('done'), key: 'done' },
                      { displayName: $t('confirmed'), key: 'confirmed' },
                    ]"
                    :label="$t('selectStatus')"
                    item-text="displayName"
                    item-value="key"
                  ></v-autocomplete>
                </v-flex>
              </template>
              <template v-slot:dataTable>
                <v-data-table item-key="_key" expand  :headers="headers" :items="forecasts" hide-actions>
                  <template v-slot:expand="props">
                  <ForecastTableExpand :item="props.item"></ForecastTableExpand>
                  </template>
                  <template v-slot:headerCell="props">
                    <template v-if="props.header.text === 'quantity'">
                      <v-flex>{{ $t('forecast') }}</v-flex>
                      <v-flex>{{ $t('inboundQuantity') }}</v-flex>
                    </template>
                    <template v-else-if="props.header.text === 'time'">
                      <v-flex>{{ $t('createTime') }}</v-flex>
                      <v-flex>{{ $t('lastModifiedTime') }}</v-flex>
                    </template>
                    <template v-else>{{ props.header.text }}</template>
                  </template>
                  <template v-slot:items="props">
                    <tr @click="props.expanded = !props.expanded">
                      <td class="text-xs-center">{{ props.index + 1 }}</td>
                    <td class="text-xs-left">{{ props.item._key }}</td>
                    <td class="text-xs-left">
                      <div :style="props.item.isDisabledOrg ?'background-color: yellow': ''">
                        {{
                          isTenant
                            ? `${getWarehouseName(props.item.warehouseKey)}`
                            : `${getOrganizationName(props.item.organizationKey, props.item)}`
                        }}
                      </div>
                      <div>
                        {{ getWarehouseSiteName(props.item.warehouseKey, props.item.warehouseSite) }}
                      </div>
                    </td>
                    <td class="text-xs-left">
                        {{calculateTotalQty(props.item.pkgItems)}}
                      </td>
                      <td class="text-xs-left">
                        {{calculateSkuOfNum(props.item.pkgItems)}}
                      </td>
                      <td class="text-xs-left">
                        {{calculateBoxQty(props.item.pkgItems)}}
                      </td>
                    <td class="text-xs-left">
                      {{ $t(props.item.status === 'ready' ? 'readyLower' : props.item.status) }}
                    </td>
                    <td class="text-xs-left">
                        {{ props.item.shipType || 'N/A' }}
                      </td>
                    <td>
                      <div>{{ toDateYYYYMMDD(props.item.createTime) }}</div>
                      <div>{{ toDateYYYYMMDD(props.item.lastModifiedTime) }}</div>
                    </td>
                    <td style="white-space: pre-wrap; word-break: break-word; max-width: 300px; white-space: pre-line">
                      {{ props.item.note }}
                    </td>
                    </tr>
                  </template>
                </v-data-table>
              </template>
            </PaginationController>
          </div>
          <div v-if="tab === 2 && index === 2">
            <PaginationController
              v-model="beforeShipments"
              getDataActionName="getBeforeShipmentForecasts"
              haveStartDate
              haveSearchBox
              keepKeywordCase
              :select="selectedValue"
              :startDateLabel="$t('processStartDate')"
              :searchBoxLabel="`${$t('tracking')} / ${$t('upc')} / sku`"
              :historyLimit="historyLimit"
            >
              <template v-slot:beforeSearchBox>
                <v-flex md4 v-if="isTenant">
                  <v-autocomplete
                    v-model="selectedValue"
                    :items="sitesSelections"
                    :label="$t('selectSite')"
                    item-text="siteName"
                    item-value="key"
                  ></v-autocomplete>
                </v-flex>
              </template>
              <template v-slot:beforeMenu>
                <v-layout row v-if="isTenant">
                  <v-flex class="headline">
                    <v-btn :disabled="!selectedPackages.length" color="primary" @click="showCreateShipmentDialog()">
                      <v-icon dark>add</v-icon> {{ $t('completeShipment') }}
                    </v-btn>
                  </v-flex>
                  <v-flex class="headline">
                    <v-btn :disabled="!selectedPackages.length" color="primary" @click="showCreateShipmentDialog(true)">
                      <v-icon dark>add</v-icon> {{ $t('splitShipment') }}
                    </v-btn>
                  </v-flex>
                </v-layout>
              </template>
              <template v-slot:dataTable>
                <v-data-table
                  :select-all="isTenant && 'blue'"
                  v-model="selectedPackages"
                  :pagination.sync="pagination"
                  item-key="tracking"
                  :rows-per-page-items="rowPerPage"
                  :headers="shipmentHeaders"
                  :items="beforeShipmentPackages"
                >
                  <template v-slot:items="props">
                    <td class="checkbox-align-center" v-if="isTenant">
                      <v-layout>
                        <v-checkbox
                          @click.stop="
                            () => {
                              props.selected = !props.selected
                            }
                          "
                          :value="props.selected"
                          primary
                          hide-details
                        ></v-checkbox>
                      </v-layout>
                    </td>
                    <td class="text-xs-center">{{ props.index + 1 }}</td>
                    <td class="text-xs-left">{{ props.item.forecastDocKey }}</td>
                    <td class="text-xs-left">
                      <div :style="props.item.isDisabledOrg ?'background-color: yellow': ''">
                        {{
                          isTenant
                            ? `${getWarehouseName(props.item.warehouseKey)}`
                            : `${getOrganizationName(props.item.organizationKey, props.item)}`
                        }}
                      </div>
                      <div>
                        {{ getWarehouseSiteName(props.item.warehouseKey, props.item.warehouseSite) }}
                      </div>
                    </td>
                    <td class="text-xs-left">
                      {{ props.item.tracking }}
                    </td>
                    <td class="text-xs-left">
                      {{
                        props.item.isStandardSmallBox
                          ? $t('standardSmall')
                          : `${props.item.length || 0}/${props.item.width || 0}/${props.item.height || 0} ${$t('inch')}`
                      }}
                    </td>
                    <td class="text-xs-left">
                      <div v-for="(pkg, index) in props.item.upcs" :key="String(pkg.upc + index)">
                        <v-layout>
                          <v-flex >{{ pkg.upc }}</v-flex>
                          <v-flex pl-4 class="text-xs-right">{{ pkg.inboundQty || 0}} / {{pkg.inboundQty - (pkg.outboundQty || 0)}}</v-flex>
                        </v-layout>
                      </div>
                    </td>
                    <td class="text-xs-left">
                      {{ $t(props.item.packageStatus === 'ready' ? 'readyLower' : props.item.packageStatus) }}
                    </td>
                    <td>
                      <div>{{ toDateYYYYMMDD(props.item.warehousingTime) }}</div>
                    </td>
                    <td class="text-xs-center">
                      <v-tooltip top>
                        <template v-slot:activator="tooltip">
                          <div v-on="tooltip.on">
                            <v-btn :loading="shipmentLoading && props.item.isLoading" flat icon @click.stop="removeAllOutbound(props.item)" color="primary">
                              <v-icon>archive</v-icon>
                            </v-btn>
                          </div>
                        </template>
                        {{ $t('removeShippedPkg') }}
                      </v-tooltip>
                    </td>
                  </template>
                </v-data-table>
              </template>
            </PaginationController>
            <ForecastAddShipment
              :selectedPackages="selectedPackages"
              v-model="isShowShipmentDialog"
              v-if="isShowShipmentDialog"
              @updateBeforeShipments="updateBeforeShipments"
              :isSplitShipment="isSplitShipment"
              @closeCreateShipments="closeCreateShipments"
            />
          </div>
          <div v-if="tab === 3 && index === 3">
            <v-flex md2 right>
              <v-text-field
                append-icon="filter_list"
                :label="$t('search')"
                single-line
                clearable
                hide-details
                v-model.trim="search"
              ></v-text-field>
            </v-flex>
            <v-data-table
              :headers="pendingShipmentHeaders"
              :search="search"
              :items="shipments"
              :pagination.sync="pendingShipmentPagination"
              :rows-per-page-items="rowPerPage"
            >
              <template v-slot:items="props">
                <td class="text-xs-center">{{ props.index + 1 }}</td>
                <td class="text-xs-left">{{ props.item._key }}</td>
                <td class="text-xs-left">
                  <div :style="props.item.isDisabledOrg ?'background-color: yellow': ''">
                    {{
                      isTenant
                        ? `${getWarehouseName(props.item.warehouseKey)}`
                        : `${getOrganizationName(props.item.tenantKey, props.item)}`
                    }}
                  </div>
                  <div>
                    {{ getWarehouseSiteName(props.item.warehouseKey, props.item.products[0].warehouseSite) }}
                  </div>
                </td>
                <td class="text-xs-left">
                  {{ props.item.trackingNum }}
                </td>
                <td class="text-xs-left">
                  <div v-for="(pkg, index) in props.item.products" :key="String(pkg.upc + index)">
                    <v-layout>
                      <v-flex >{{ pkg.upc }}</v-flex>
                      <v-flex pl-4 class="text-xs-right">{{ pkg.toShip || 0}}</v-flex>
                    </v-layout>
                  </div>
                </td>
                <td class="text-xs-left">
                  {{ $t(props.item.status === 'ready' ? 'readyLower' : props.item.status) }}
                </td>
                <td>
                  <div>{{ toDateYYYYMMDD(props.item.createTime) }}</div>
                </td>
                <td v-if="!isWarehouse">
                  <v-tooltip top>
                    <template v-slot:activator="tooltip">
                      <div v-on="tooltip.on">
                        <v-btn
                          :loading="shipmentLoading && props.item.isLoading"
                          flat
                          @click.stop="cancelShipment(props.item)"
                          color="primary"
                        >
                          {{ $t('delete') }}
                        </v-btn>
                      </div>
                    </template>
                    {{ $t('cancel') }}
                  </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </div>
          <div v-if="tab === 4 && index === 4">
            <PaginationController
              v-model="shippedShipments"
              getDataActionName="getShippedForecastShipments"
              haveStartDate
              haveSearchBox
              keepKeywordCase
              isUsingModelInAction
              :select="selectedStatus"
              :startDateLabel="$t('processStartDate')"
              :isRefreshSelect="false"
              :searchBoxLabel="`${$t('tracking')} / ${$t('upc')} / sku`"
              :historyLimit="historyLimit"
            >
              <template v-slot:dataTable>
                <v-data-table
                  :pagination.sync="pagination"
                  item-key="_key"
                  :rows-per-page-items="rowPerPage"
                  :headers="shippedShipmentHeaders"
                  :items="shippedShipments"
                >
                  <template v-slot:items="props">
                    <td class="text-xs-center">{{ props.index + 1 }}</td>
                    <td class="text-xs-left">{{ props.item._key }}</td>
                    <td class="text-xs-left">
                      <div :style="props.item.isDisabledOrg ?'background-color: yellow': ''">
                        {{
                          isTenant
                            ? `${getWarehouseName(props.item.warehouseKey)}`
                            : `${getOrganizationName(props.item.tenantKey, props.item)}`
                        }}
                      </div>
                      <div>
                        {{ getWarehouseSiteName(props.item.warehouseKey, props.item.products[0].warehouseSite) }}
                      </div>
                    </td>
                    <td class="text-xs-left">
                      {{ props.item.trackingNum }}
                    </td>
                    <td class="text-xs-left">
                      <div v-for="(pkg, index) in props.item.products" :key="String(pkg.upc + index)">
                        <v-layout>
                          <v-flex >{{ pkg.tracking }}</v-flex>
                          <v-flex pl-4 class="right text-xs-right">{{ pkg.toShip || 0}}</v-flex>
                        </v-layout>
                      </div>
                    </td>
                    <td style="white-space: pre-wrap; word-break: break-word; max-width: 300px; white-space: pre-line">
                      {{ props.item.note }}
                    </td>
                    <td class="text-xs-left">
                      <div>{{ toDateYYYYMMDD(props.item.createTime) }}</div>
                    </td>
                  </template>
                </v-data-table>
              </template>
            </PaginationController>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
  </v-container>
</template>

<script>
import ForecastDetailPopup from './ForecastDetailPopup'
import PaginationController from './PaginationController'
import ForecastAddShipment from './ForecastAddShipment'
import DropshippingForecastPopup from './DropshippingForecastPopup'
import { checkRules, timeTools, cloneDeep, downloadBufferFile } from '@/utils/tools'
import ForecastTableExpand from './ForecastTableExpand'

export default {
  name: 'ForecastPackage',
  mixins: [checkRules, timeTools],
  components: {
    ForecastDetailPopup,
    PaginationController,
    ForecastAddShipment,
    DropshippingForecastPopup,
    ForecastTableExpand
  },
  data () {
    return {
      isShowForecastDialog: false,
      forecast: {
        warehouseSite: {},
        shipType: '',
        curWarehouse: this.forecastWarehouses,
        note: '',
        pkgItems: []
      },
      forecasts: [],
      historyLimit: 25,
      isAddForecast: false,
      selectedValue: '',
      menu: [],
      tab: null,
      selectedStatus: '',
      beforeShipments: [],
      beforeShipmentPackages: [],
      isShowShipmentDialog: false,
      pagination: {
        sortBy: 'warehousingTime'
      },
      pendingShipmentPagination: {
        sort: 'createTime'
      },
      selectedPackages: [],
      warehouseTabs: [this.$t('forecast'), this.$t('history'), this.$t('availableToShip'), this.$t('pendingShipment'), this.$t('shipped')],
      shipmentLoading: false,
      shippedShipments: [],
      isSplitShipment: false,
      search: ''
    }
  },
  mounted () {
  },
  watch: {
    tab () {
      this.selectedValue = ''
      this.selectedPackages = []
      this.selectedStatus = ''
    },
    forecasts (val) {
      if (Array.isArray(val) && val.length >= this.forecasts.length) {
        val.forEach(pkg => {
          pkg.pkgItems = Object.keys(pkg.packages).reduce((mergeItems, key) => {
            if (!pkg.packages[key].upcs) return mergeItems
            let item = pkg.packages[key].upcs.map(item => { return {...item, ...pkg.packages[key], tracking: pkg.packages[key].tracking || key} })
            return [...mergeItems, ...item]
          }, []).sort((a, b) => {
            return a.tracking > b.tracking ? 1 : -1
          })
        })
      }
    },
    beforeShipments (val) {
      if (Array.isArray(val) && val.length >= this.beforeShipments.length) {
        this.beforeShipmentPackages = this.transformData(val, 'ready')
      }
    }
  },
  computed: {
    tabs () {
      if (this.isOpenPackageManageOrg || !!this.tenantInfo.closePKGModeTime) {
        return [ this.$t('forecast'), this.$t('history'), this.$t('availableToShip'), this.$t('pendingShipment'), this.$t('shipped') ]
      }
      return [ this.$t('forecast'), this.$t('history') ]
    },
    shippedShipmentHeaders () {
      return [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('shipmentID'), align: 'left', value: '_key', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false },
        { text: this.$t('tracking'), value: 'trackingNum', align: 'left', sortable: false },
        { text: `${this.$t('upc')} / sku <--> ${this.$t('Quantity')}`, width: '12%', align: 'left', sortable: false },
        { text: this.$t('note'), align: 'left', sortable: false, width: '14%' },
        { text: this.$t('createTime'), align: 'left', sortable: false }
      ]
    },
    allWarehouseSites () {
      return this.warehouses.filter(({warehouseKey}) => this.openForecastedWarehouseKey.includes(warehouseKey))
        .reduce((total, warehouse) => {
          return [...total, ...warehouse.sites]
        }, [])
    },
    sitesSelections () {
      return [{siteName: `-- ${this.$t('all')} --`, key: ''}, ...this.allWarehouseSites]
    },
    isOpenPackageManageOrg () {
      return !!this.tenantInfo?.featureInboundForecastPkg?.length
    },
    shipments () {
      return this.$store.getters.shipmentsAndDrafts?.filter(shipments => shipments.isForecastedPackages) || []
    },
    products () {
      return this.$store.getters.productsWithUpcChangeableFlag || []
    },
    organizationSelections () {
      return [{displayName: `-- ${this.$t('all')} --`, organizationId: '', key: ''}, ...this.organizations]
    },
    forecastOrgKeys () {
      const {featureInboundForecast = [], featureInboundForecastPkg = [], featureInboundForecastDropshipping = []} = this.$store.getters.warehouseInfo || {}
      return [...new Set([...featureInboundForecast, ...featureInboundForecastPkg, ...featureInboundForecastDropshipping])]
    },
    organizations () {
      return this.$store.getters.sortedWarehouseOrganizations?.filter(element => {
        return element.organizationId !== '' && this.forecastOrgKeys.includes(element.key)
      }) || []
    },
    warehousesSites () {
      return this.$store.getters.warehousesSites || []
    },
    warehouses () { 
      return this.$store.getters.warehouses || []
    },
    isTenant () {
      return !!this.$store.getters.activeOrganization
    },
    isWarehouse () {
      return !!this.$store.getters.activeWarehouse
    },
    shipmentHeaders () {
      const warehouseHeaders = this.tab === 2 ? [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('id#'), align: 'left', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('tracking'), value: 'tracking', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, sortable: false, align: 'left' },
        { text: `${this.$t('upc')} / sku <--> ${this.$t('Quantity')} / ${this.$t('available')}`, width: '20%', align: 'left', sortable: false },
        { text: this.$t('status'), align: 'left', sortable: false },
        { text: this.$t('inboundTime'), align: 'left', sortable: false },
        { text: this.$t('actions'), align: 'center', sortable: false, width: '6%' }
      ] : [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('id#'), align: 'left', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('tracking'), value: 'tracking', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, sortable: false, align: 'left' },
        { text: `${this.$t('upc')} / sku <--> ${this.$t('Quantity')}`, width: '20%', align: 'left', sortable: false },
        { text: this.$t('status'), align: 'left', sortable: false },
        { text: this.$t('createTime'), align: 'left', sortable: false }
      ]
      const tenantHeaders = [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('id#'), align: 'left', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('tracking'), value: 'tracking', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, sortable: false, align: 'left' },
        { text: `${this.$t('upc')} / sku <--> ${this.$t('Quantity')} / ${this.$t('available')}`, width: '20%', align: 'left', sortable: false },
        { text: this.$t('status'), align: 'left', sortable: false },
        { text: this.$t('inboundTime'), align: 'left', sortable: false },
        { text: this.$t('actions'), align: 'center', sortable: false, width: '6%' }
      ]
      this.tab === 4 && tenantHeaders.pop()
      return this.isWarehouse ? warehouseHeaders : tenantHeaders
    },
    pendingShipmentHeaders () {
      return this.isWarehouse ? [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('shipmentID'), align: 'left', value: '_key', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false },
        { text: this.$t('tracking'), value: 'trackingNum', align: 'left', sortable: false },
        { text: `${this.$t('upc')} / sku <--> ${this.$t('Quantity')}`, width: '20%', align: 'left', sortable: false },
        { text: this.$t('status'), align: 'left', sortable: false },
        { text: this.$t('createTime'), align: 'left', sortable: false }
      ] : [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('shipmentID'), align: 'left', value: '_key', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false },
        { text: this.$t('tracking'), value: 'trackingNum', align: 'left', sortable: false },
        { text: `${this.$t('upc')} / sku <--> ${this.$t('Quantity')}`, width: '20%', align: 'left', sortable: false },
        { text: this.$t('status'), align: 'left', sortable: false },
        { text: this.$t('createTime'), align: 'left', sortable: false },
        { text: this.$t('actions'), align: 'center', value: 'keywordString', sortable: false, width: '6%' }
      ]
    },
    headers () {
      const headers = [
        { text: '#', sortable: false, value: '', align: 'center', width: 30 },
        { text: this.$t('id#'), align: 'left', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('itemQty'), align: 'left', sortable: false },
        { text: this.$t('numOfSku'), align: 'left', sortable: false },
        { text: this.$t('boxes'), align: 'left', sortable: false },
        { text: this.$t('status'), align: 'left', sortable: false },
        { text: this.$t('shipType'), align: 'left', sortable: false },
        { text: `time`, align: 'left', sortable: false },
        { text: this.$t('note'), align: 'left', sortable: false, width: '14%' }
      ]
      if (this.tab !== 1) {
        headers.push({ text: this.$t('actions'), align: 'center', sortable: false, width: '6%' })
      }
      return headers
    },
    rowPerPage () {
      return [20, 50, 100, {text: this.$t('all'), value: -1}]  
    },
    openForecastedWarehouseKey () {
      const {featureInboundForecast = [], featureInboundForecastPkg = [], featureInboundForecastDropshipping = []} = this.tenantInfo
      return [...new Set([...featureInboundForecast, ...featureInboundForecastPkg, ...featureInboundForecastDropshipping])]
    },
    featureInboundForecastDropshipping () {
      return this.tenantInfo.featureInboundForecastDropshipping || []
    },
    tenantInfo () {
      return this.$store.getters.tenant || {}
    },
    forecastWarehouses () {
      return this.warehouses.find(warehouse => this.openForecastedWarehouseKey.includes(warehouse.warehouseKey)) || {}
    },
    isWarehouseOwner () {
      const { roles = [] } = this.$store.getters.user || {}
      return roles.includes('warehouseOwner')
    }
  },
  methods: {
    calculateTotalQty (upcs = []) {
      return upcs.reduce((total, {forecastQty = 0}) => {
        return total + forecastQty
      }, 0)
    },
    calculateSkuOfNum (boxes) {
      const set = new Set()
      boxes.forEach(box => {
        set.add(box.upc)
      })
      return set.size
    },
    calculateBoxQty (boxes) {
      const set = new Set()
      boxes.forEach(box => {
        set.add(box.boxMark || box.tracking || '')
      })
      return set.size
    },
    closeCreateShipments () {
      this.isShowShipmentDialog = false
      this.isSplitShipment = false
    },
    cancelShipment (item) {
      this.shipmentLoading = true 
      item.isLoading = true
      if (!item._key) {
        this.$store.dispatch('showToast', { info: 'shipment doc missing', level: 'error' })
        item.isLoading = false
        this.shipmentLoading = false 
        throw new Error('shipment doc missing')
      }
      if (confirm(this.$t((item.labels && item.labels.length > 0) ? 'cancelShipment' : 'cancelShipmentNoItem'))) {
        return this.$store.dispatch('cancelShipment', item)
          .then(() => {
            this.$store.dispatch('showToast', { info: this.$t('success'), level: 'success' })
          })
          .catch((error) => {
            this.$store.dispatch('showToast', { info: error, level: 'error' })
            item.isLoading = false
            throw error
          })
          .finally(() => {
            this.shipmentLoading = false 
          })
      }
      this.shipmentLoading = false
      item.isLoading = false
      return Promise.resolve()
    },
    transformData (arr = [], ...status) {
      const data = arr.reduce((packages, pkg) => {
        let curPackages = Object.keys(pkg.packages).reduce((pkgs, key) => {
          const { upcs = [], packageStatus, ...rest } = pkg.packages[key]
          if (status.includes(packageStatus)) {
            pkgs.push({
              ...rest,
              tracking: key,
              forecastDocKey: pkg._key,
              upcs: upcs,
              packageStatus: packageStatus,
              warehouseKey: pkg.warehouseKey,
              warehouseSite: pkg.warehouseSite,
              organizationKey: pkg.organizationKey,
              lastModifiedTime: pkg.lastModifiedTime
            })
          }
          return pkgs
        }, []) || []
        return [...packages, ...curPackages]
      }, [])
      return data
    },
    removeAllOutbound (item = {}) {
      if (prompt(this.$t('removeForecastPKGAlert')) !== 'ok') return
      this.shipmentLoading = true 
      item.isLoading = true
      return this.$store.dispatch('removeForecastPkgToShipped', item)
        .then(() => {
          this.beforeShipmentPackages = this.beforeShipmentPackages.filter(pkg => {
            return pkg.tracking !== item.tracking
          })
          this.$store.dispatch('showToast', { info: this.$t('success'), level: 'success' })
        })
        .catch((error) => {
          this.$store.dispatch('showToast', { info: error, level: 'error' })
          throw error
        })
        .finally(() => {
          this.shipmentLoading = false 
          item.isLoading = false
        })
    },
    updateBeforeShipments (pkgs = []) {
      if (this.isSplitShipment) {
        pkgs.forEach(pkg => {
          let matchedIndex = this.beforeShipmentPackages.findIndex(({tracking}) => pkg.tracking === tracking)
          if (matchedIndex !== -1) {
            let upcs = this.beforeShipmentPackages[matchedIndex].upcs.map(upcItem => {
              let matchedUpc = pkg.upcs.find(({upc}) => upcItem.upc === upc)
              upcItem.outboundQty = (matchedUpc.outboundQty || 0) + matchedUpc.toShip
              return upcItem
            })
            if (upcs.some((upc) => upc.inboundQty - upc.outboundQty)) {
              this.$set(this.beforeShipmentPackages, matchedIndex, {...this.beforeShipmentPackages[matchedIndex], upcs})
            } else {
              this.$delete(this.beforeShipmentPackages, matchedIndex)
            }
          }
        })
      } else {
        this.beforeShipmentPackages = this.beforeShipmentPackages.filter(item => {
          return !this.selectedPackages.some(pkg => {
            return pkg.forecastDocKey === item.forecastDocKey
          })
        })
      }
    },
    showCreateShipmentDialog (isSplitShipment = false) {
      if (this.selectedPackages.length > 1 && this.selectedPackages.some((pkg, idx, arr) => 
        pkg.warehouseSite !== arr[0].warehouseSite)) {
        return alert(this.$t('confirmMergePkg'))
      }
      isSplitShipment && (this.isSplitShipment = true)
      this.isShowShipmentDialog = true
    },
    getProductName (upc) {
      return this.products.find(product => product.upc === upc || product.sku?.includes(upc))?.name
    },
    updateForecast (forecast = {}) {
      let index = this.forecasts.findIndex(item => item._key === forecast.key)
      if (index !== -1) {
        forecast.pkgItems = Object.keys(forecast.packages).reduce((mergeItems, key) => {
          let item = forecast.packages[key].upcs.map(item => { return {...item, tracking: key} })
          return [...mergeItems, ...item]
        }, []).sort((a, b) => {
          return a.tracking > b.tracking ? 1 : -1
        })
        if (forecast.status === 'done') return this.$delete(this.forecasts, index)
        this.$set(this.forecasts, index, {...this.forecasts[index], ...forecast})
      }
    },
    getOrgId (warehouseKey) {
      return (this.warehouses.find(warehouse => warehouseKey === warehouse.warehouseKey) || {}).orgId
    },
    async printBoxMarks (row) {
      if (row.isDClass) {
        this.$set(row, 'isPrinting', true)
        try {
          const pkgs = Object.values(row.packages).sort((a, b) => {
            const lastPartA = a.boxMark.split('-').pop()
            const lastPartB = b.boxMark.split('-').pop()

            const numA = parseInt(lastPartA, 10)
            const numB = parseInt(lastPartB, 10)

            if (!isNaN(numA) && !isNaN(numB)) {
              return numA - numB
            } else {
              return lastPartA.localeCompare(lastPartB)
            }
          })
          const len = pkgs.length
          const orgId = this.getOrgId(row.warehouseKey)
          const warehouseName = this.getWarehouseName(row.warehouseKey)
          const payload = {
            barcodes: pkgs.map((item, index) => {
              const skuQty = item.upcs.length
              const totalQty = item.upcs.reduce((total, upc) => total + upc.forecastQty, 0)
              return {
                code: item.boxMark,
                text: item.boxMark,
                boxNum: Number(index) + 1,
                totalNumber: len,
                warehouseName: warehouseName,
                skuQty: skuQty,
                orgId: orgId,
                totalQty: totalQty
              }
            }),
            type: 'boxMark'
          }
          const response = await this.$store.dispatch('generateBarcodes', payload)
          return downloadBufferFile(response.data, `${Date.now()}.pdf`)
        } catch (error) {
          this.$store.dispatch('showToast', { info: error, level: 'error' })
          throw error
        } finally {
          this.$set(row, 'isPrinting', false)
        }
      }
    },
    addForecast (forecast = {}) {
      this.forecasts.unshift(forecast)
    },
    confirmForecast (forecast = {}) {
      if (!confirm(this.$t('forecastConfirmWarn'))) return
      this.$set(forecast, 'isLoading', true)
      return this.$store.dispatch('confirmForecast', forecast._key)
        .then(() => {
          this.forecasts = this.forecasts.filter(item => forecast._key !== item._key)
        })
        .finally(() => {
          forecast.isLoading = false
        })
    },
    deleteForecast (forecast = {}) {
      if (!confirm(this.$t('confirmDeleteForecast'))) return
      this.$set(forecast, 'isDelLoading', true)
      let { pkgItems = [], _key } = forecast 
      let trackings = pkgItems.map(pkg => pkg.tracking.toUpperCase())
      return this.$store.dispatch('deleteForecast', {key: _key, trackings})
        .then(() => {
          this.forecasts = this.forecasts.filter(item => _key !== item._key)
        })
        .finally(() => {
          forecast.isDelLoading = false
        })
    },
    addNewForecast () {
      this.isShowForecastDialog = true
      this.forecast = {
        warehouseSite: this.forecastWarehouses?.sites?.[0]?.key || '',
        shipType: 'ground',
        curWarehouse: this.forecastWarehouses || {},
        note: '',
        pkgItems: []
      }
      this.isAddForecast = true
    },
    showDetailDialog (forecast = {}) {
      this.isShowForecastDialog = true
      forecast = cloneDeep(forecast)
      let { pkgItems = [], isDClass } = forecast
      forecast.pkgItems = pkgItems.reduce((mergeItems, item) => {
        let {upc, inboundQty = 0, forecastQty = 0, tracking, productName = '', ...rest} = item
        let index = mergeItems.findIndex((mergeItem) => mergeItem.tracking === tracking)
        if (index === -1) {
          mergeItems.push({...rest, tracking, upcs: [{inboundQty, forecastQty, upc, productName}]})
        } else {
          mergeItems[index].upcs.push({inboundQty, forecastQty, upc, productName})
        }
        return mergeItems
      }, [])
      if (isDClass) {
        forecast.pkgItems = forecast.pkgItems.reduce((mergeItems, item) => {
          let index = mergeItems.findIndex((mergeItem) => {
            const isDClassMatched = mergeItem.upcs.length === item.upcs.length
            if (isDClassMatched) {
              const upcMatched = JSON.stringify(mergeItem.upcs) === JSON.stringify(item.upcs)
              return upcMatched
            }
            return false
          })
          if (index === -1) {
            item.pkgQty = 1
            mergeItems.push(item)
          } else {
            mergeItems[index].pkgQty += 1
          }
          return mergeItems
        }, [])
      }
      this.forecast = forecast
    },
    getWarehouseSiteName (warehouseKey, warehouseSite) {
      return this.isTenant ?
        (this.warehouses.find(warehouse => warehouseKey === warehouse.warehouseKey) || {})
          .sites?.find?.(site => site.key === warehouseSite)?.siteName || '' : 
        (this.warehousesSites.find(site => site.key === warehouseSite) || {}).siteName || ''
    },
    getWarehouseName (warehouseKey) {
      return (this.warehouses.find(warehouse => warehouseKey === warehouse.warehouseKey) || {}).warehouseName
    },
    getOrganizationName (organizationKey, item) {
      let name = (this.$store.getters.sortedWarehouseOrganizations.find(organization => organizationKey === organization.key) || {}).tenantName
      !this.forecastOrgKeys.includes(organizationKey) && this.$set(item, 'isDisabledOrg', true)
      return name
    },
    closeForecastDialog () {
      this.isShowForecastDialog = false
      this.forecast = {}
      this.isAddForecast = false
    }
  }
}
</script>

<style scoped>
.partial_highlight {
  background-color: yellow;
}
.textOverflowHide {
  max-width: 200px;
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/ForecastPackage.vue